import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useEffect, useContext, useState } from 'react'
import { throttle } from 'lodash'
import {
  CMasthead,
  CMastheadFragments,
  CMastheadBlock,
  CSeomatic,
  SeomaticData,
  CCtaBox,
  CCtaBoxBlock,
  CCtaBoxFragments,
  CSplitTestimonialFragments,
  CFullWidthTestimonialFragments,
  CTestimonialsSliderFragments,
  CSmallTestimonialGridFragments,
  CSplitTestimonialBlock,
  CFullWidthTestimonialBlock,
  CSmallTestimonialGridBlock,
  CTestimonialsSliderBlock,
  CSplitTestimonial,
  CFullWidthTestimonial,
  CFindADoctor,
  CSmallTestimonialGrid,
  CTestimonialsSlider,
  CFaqSlider,
  CCenteredCopy,
  CStickyDisclaimerBlock,
  CStickyDisclaimerFragments,
  CStickyDisclaimer,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { HeaderContext } from 'ui/context/header'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')

  if (siteInfo.code === 'en-IN') {
    return {
      notFound: true,
    }
  }

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_lifeWithVisian_Entry {
            masthead {
              ...masthead
            },
            splitTestimonial {
              ...splitTestimonial
            },
            fullWidthTestimonial {
              ...fullWidthTestimonial
            },
            splitTestimonial2 {
              ...splitTestimonial2
            },
            fullWidthTestimonial2 {
              ...fullWidthTestimonial2
            },
            smallTestimonialGrid {
              ...smallTestimonialGrid
            },
            testimonialsSlider {
              ...testimonialsSlider
            },
            ctaBox {
              ...ctaBox
            }
            stickyDisclaimer {
              ...stickyDisclaimer
            }
          }
        }
      }
      ${CMastheadFragments.field}
      ${CSplitTestimonialFragments.field}
      ${CFullWidthTestimonialFragments.field}
      ${CSplitTestimonialFragments.field2}
      ${CFullWidthTestimonialFragments.field2}
      ${CSmallTestimonialGridFragments.field}
      ${CTestimonialsSliderFragments.field}
      ${CCtaBoxFragments.field}
      ${CStickyDisclaimerFragments.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      transparentHeader: true,
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    splitTestimonial: Array<CSplitTestimonialBlock>
    fullWidthTestimonial: Array<CFullWidthTestimonialBlock>
    splitTestimonial2: Array<CSplitTestimonialBlock>
    fullWidthTestimonial2: Array<CFullWidthTestimonialBlock>
    smallTestimonialGrid: Array<CSmallTestimonialGridBlock>
    testimonialsSlider: Array<CTestimonialsSliderBlock>
    ctaBox: Array<CCtaBoxBlock>
    stickyDisclaimer: Array<CStickyDisclaimerBlock>
  }
  global: CraftGlobals
  transparentHeader: boolean
}

export default function LifeWithVisian({ entry, global }: Props) {
  const [isScrollAtTop, setIsScrollAtTop] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const mobileBreakpoint = 768

  const headerContext = useContext(HeaderContext)

  const handleWindowScroll = () => {
    setIsScrollAtTop(window.pageYOffset === 0)
  }

  const handleWindowResize = () => {
    setIsMobile(window.innerWidth < mobileBreakpoint)
  }

  const handleWindowScrollThrottled = throttle(handleWindowScroll, 100)
  const handleWindowResizeThrottled = throttle(handleWindowResize, 100)

  useEffect(() => {
    handleWindowResize()
    window.addEventListener('scroll', handleWindowScrollThrottled)
    window.addEventListener('resize', handleWindowResizeThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
      window.removeEventListener('resize', handleWindowResizeThrottled)
    }
  }, [])

  useEffect(() => {
    headerContext?.setIsClear(!isMobile && isScrollAtTop)
  }, [isScrollAtTop, isMobile])

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CMasthead field={entry.masthead} />
      <CSplitTestimonial field={entry.splitTestimonial} />
      <CFullWidthTestimonial field={entry.fullWidthTestimonial} />
      <CFindADoctor field={global.findADoctor} margins={true} />
      <CSplitTestimonial field={entry.splitTestimonial2} />
      <CFullWidthTestimonial field={entry.fullWidthTestimonial2} />
      <CSmallTestimonialGrid field={entry.smallTestimonialGrid} />
      <CTestimonialsSlider field={entry.testimonialsSlider} />
      <CCtaBox field={entry.ctaBox} />
      <CFaqSlider field={global.faqVideoSlider} />
      <CCenteredCopy field={global.centeredCopy} />
      <CStickyDisclaimer field={entry.stickyDisclaimer} />
    </DefaultLayout>
  )
}
